// Stats section híbrida: 2 stats de marketing (margen recuperado, horas ahorradas)
// + 2 stats LIVE del backend (precios scrapeados, tiendas monitoreadas).
// Los live se hidratan de /api/public/landing-stats con cache 60s.

import { ScrollReveal } from '@/components/motion/scroll-reveal';
import { AnimatedCounter } from '@/components/motion/animated-counter';
import { Activity, Store, TrendingUp, Clock } from 'lucide-react';
import { useLandingStats } from '../hooks/use-landing-stats';

interface StatCardData {
  icon: typeof Activity;
  value: number;
  suffix: string;
  label: string;
  sub?: string;
  live?: boolean;
}

function StatCard({ stat }: { stat: StatCardData }) {
  const Icon = stat.icon;
  return (
    <div className="text-center">
      <div className="inline-flex items-center justify-center gap-1.5 mb-3">
        <Icon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
        {stat.live && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            En vivo
          </span>
        )}
      </div>
      <p className="text-4xl sm:text-5xl font-bold mb-2">
        <AnimatedCounter
          target={stat.value}
          suffix={stat.suffix}
          decimals={stat.value % 1 !== 0 ? 1 : 0}
          duration={2}
          className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-violet-600"
        />
      </p>
      <p className="text-sm text-gray-700 dark:text-gray-200 font-semibold">{stat.label}</p>
      {stat.sub && <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{stat.sub}</p>}
    </div>
  );
}

function fmtRelative(iso: string | null): string {
  if (!iso) return 'hace instantes';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.max(0, Math.round(ms / 60000));
  if (m < 1) return 'ahora mismo';
  if (m < 60) return `hace ${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `hace ${h} h`;
  return `hace ${Math.round(h / 24)} d`;
}

export function StatsSection() {
  const { stats: live } = useLandingStats();

  const tiendas = live?.activeStores || 4;

  const cards: StatCardData[] = [
    {
      icon: Activity,
      value: live?.competitorPrices || 3700,
      suffix: '+',
      label: 'Precios de competencia scrapeados',
      sub: live?.lastScrapeAt
        ? `Último scrape ${fmtRelative(live.lastScrapeAt)}`
        : 'Actualizado cada hora',
      live: true,
    },
    {
      icon: Store,
      // El servidor redondea hacia abajo y no cuenta demos: con 0 se muestra el valor de siempre, y desde 10 el
      // número lleva «+» (es un piso, no una cuenta exacta), igual que el de precios.
      value: tiendas,
      suffix: tiendas >= 10 ? '+' : '',
      label: 'Tiendas competidoras monitoreadas',
      sub: 'En tiempo real',
      live: true,
    },
    {
      icon: TrendingUp,
      value: 8,
      suffix: '%',
      label: 'Mejora promedio de margen',
      sub: 'Reduciendo descuentos defensivos',
    },
    {
      icon: Clock,
      value: 15,
      suffix: 'h',
      label: 'Ahorradas por vendedor / mes',
      sub: 'No más Excel manual',
    },
  ];

  return (
    <section className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3">
            Los números{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-violet-600">
              hablan solos
            </span>
          </h2>
          <p className="text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
            Datos en vivo del motor de scraping + impacto promedio en empresas que migraron de Excel
            a Prixia.
          </p>
        </ScrollReveal>

        <ScrollReveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {cards.map((stat) => (
              <StatCard key={stat.label} stat={stat} />
            ))}
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
